'use client'

import { useForm, useWatch } from 'react-hook-form'
import {
  Button,
  Card,
  Container,
  Form,
  Heading,
  Icon,
  LoadingSkeleton,
  RangeSlider,
  TextField,
  Tooltip
} from '@boltenergy-be/design-system'
import { InjectionSimulationFormInputs } from './types'
import styles from 'features/injection-tariff/components/injection-simulation-form/InjectionSimulationForm.module.scss'
import { useTranslation } from 'next-i18next/pages'
import {
  DEFAULT_INJECTION,
  MIN_INJECTION,
  BASE_SIMULATION_PRICE_REQUEST,
  INJECTION_STEP,
  MAX_SLIDER_INPUT,
  MAX_INPUT,
  DEFAULT_PANELS,
  MIN_PANELS,
  MAX_PANELS,
  INJECTION_ROI_MULTIPLIER
} from 'features/injection-tariff/components/injection-simulation-form/InjectionSimulationForm.constants'
import parse from 'html-react-parser'
import { useEffect, useMemo, useState } from 'react'
import { formatAmount } from 'utils/format'
import debounce from 'debounce'
import { convertAmountOfSolarPanelsToKva, convertInjectionToAmountOfSolarPanels, convertKvaToInjection } from 'utils/conversion'
import { InjectionTariffProps } from 'features/injection-tariff/types'
import StartSimulationButton from 'components/start-simulation-button/StartSimulationButton'
import { useLazySimulatePriceQuery } from 'store/api/bolt-api'

let initializedDefaultRoI = false

const InjectionSimulationForm = ({
  initStoreForInjectionTariff,
  setSimulatedInjection
}: Pick<InjectionTariffProps, 'initStoreForInjectionTariff' | 'setSimulatedInjection'>) => {
  // Dynamic route params
  const { t } = useTranslation(['injection', 'validation', 'common'])

  // React Hook Form
  const { register, setValue, formState, control } = useForm<InjectionSimulationFormInputs>({
    defaultValues: {
      injection: DEFAULT_INJECTION,
      panels: DEFAULT_PANELS
    }
  })
  const watchInjection = useWatch({ control, name: 'injection' })
  const watchPanels = useWatch({ control, name: 'panels' })

  // Redux queries
  const [simulatePrice, { data, isFetching }] = useLazySimulatePriceQuery()

  // Local state
  const [inputType, setInputType] = useState<'injection' | 'panels'>('injection')

  // Constants
  const returnOnInjection = data ? Math.abs(data.price.electricity.totalInjection * 12) : 0

  /**
   * Submits the form with the distance input and calculates the average charging cost.
   * @param {number} distance
   */
  const simulateReturnOnInjection = async ({ injection }: InjectionSimulationFormInputs) => {
    // Clearing the number input yields NaN, which JSON.stringify sends as null & makes the price simulation API reject
    if (!Number.isFinite(injection)) return

    // Update the simulated injection in the parent component to be able to use this to start a simulation on another entry point.
    setSimulatedInjection(injection)

    await simulatePrice({
      ...BASE_SIMULATION_PRICE_REQUEST,
      meterUsage: {
        electricity: {
          consumption: {
            single: 1
          },
          injection: {
            single: injection * INJECTION_ROI_MULTIPLIER // Incorporate additional discount to show the best possible price
          }
        }
      }
    })
  }

  const debouncedSimulate = useMemo(() => debounce(simulateReturnOnInjection, 500), [])

  /**
   * Toggles the input type between 'injection' and 'panels'.
   * When toggled to 'panels', the input value is converted to the equivalent amount of solar panels based on the average production per panel.
   * When toggled back to 'injection', the input value is converted back to kWh.
   */
  const toggleInputType = () => {
    setInputType((prev) => (prev === 'injection' ? 'panels' : 'injection'))
  }

  // Initialize the calculation with the default distance
  useEffect(() => {
    if (!initializedDefaultRoI) {
      initializedDefaultRoI = true
      simulateReturnOnInjection({ injection: DEFAULT_INJECTION, panels: DEFAULT_PANELS })
    }
  }, [])

  return (
    <Container as="section" classnames={{ content: styles.root }}>
      <div className={styles['form-wrapper']}>
        <Heading as="h2" serif>
          {parse(t('calculator.title'))}
        </Heading>
        <p>{t('calculator.description')}</p>

        <Form className={styles.form}>
          <RangeSlider
            label={t('calculator.form.label', { context: inputType })}
            name="injectionSlider"
            min={MIN_INJECTION}
            max={MAX_SLIDER_INPUT}
            step={INJECTION_STEP}
            value={watchInjection || MIN_INJECTION}
            labelComponentSlot={
              <Button type="button" representation="link" onClick={() => toggleInputType()}>
                {t('calculator.form.toggle', { context: inputType })}
              </Button>
            }
            onChange={(kwh) => {
              const panels = convertInjectionToAmountOfSolarPanels(kwh)
              setValue('injection', kwh, { shouldValidate: true })
              setValue('panels', panels, { shouldValidate: true })
              debouncedSimulate({ injection: kwh, panels })
            }}
            className={styles.range}
          />

          {inputType === 'injection' ? (
            <TextField
              {...register('injection')}
              min={MIN_INJECTION}
              max={MAX_INPUT}
              grid="full"
              label={t('calculator.form.label', { context: inputType })}
              hiddenLabel
              type="number"
              value={watchInjection}
              suffix={{ as: t('calculator.form.suffix', { context: inputType }), className: 'weight-semibold' }}
              onChange={(e) => {
                const kwh = e.target.valueAsNumber
                const panels = convertInjectionToAmountOfSolarPanels(kwh)
                setValue('injection', kwh, { shouldValidate: true })
                setValue('panels', panels, { shouldValidate: true })
                debouncedSimulate({ injection: kwh, panels })
              }}
              layoutClassName={styles.distance}
              error={formState?.errors?.injection?.message}
            />
          ) : (
            <TextField
              {...register('panels')}
              min={MIN_PANELS}
              max={MAX_PANELS}
              grid="full"
              label={t('calculator.form.label', { context: inputType })}
              hiddenLabel
              type="number"
              value={watchPanels}
              suffix={{ as: t('calculator.form.suffix', { context: inputType }), className: 'weight-semibold' }}
              onChange={(e) => {
                const panels = e.target.valueAsNumber
                const kva = convertAmountOfSolarPanelsToKva(panels)
                const kwh = convertKvaToInjection(kva)
                setValue('injection', Math.ceil(kwh), { shouldValidate: true })
                setValue('panels', panels, { shouldValidate: true })
                debouncedSimulate({ injection: kwh, panels })
              }}
              layoutClassName={styles.distance}
              error={formState?.errors?.panels?.message}
            />
          )}
        </Form>
      </div>

      <Card color="vibrant" className={styles.calculations}>
        <dl>
          <dt>
            <p>{t('calculator.returnOnInjection.label')}</p>
            <Tooltip tooltipContent={<p>{t('calculator.returnOnInjection.tooltip')}</p>}>
              <Icon name="questionMarkCircle" />
            </Tooltip>
          </dt>
          <dd>
            {isFetching ? (
              <LoadingSkeleton.Rectangle width={100} height={24} />
            ) : returnOnInjection ? (
              t('calculator.returnOnInjection.value', { value: formatAmount(returnOnInjection) })
            ) : (
              '—'
            )}
          </dd>
        </dl>
        <StartSimulationButton
          text={t('calculator.cta')}
          extraOnClickAction={() => initStoreForInjectionTariff(watchInjection)}
          fromPage="injection-tariff"
          isFullwidth
          variant="layered"
          className="mt-600"
        />
      </Card>
    </Container>
  )
}

export default InjectionSimulationForm
